export type AssignmentPrincipalKind = 'user' | 'security_group' | 'm365_group';
export interface AssignmentPrincipal {
  principalKind: AssignmentPrincipalKind;
  principalIdentifier: string;
  principalEmail?: string;
}

const objectIdPattern = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
const prefixes = {
  security_group: 'c:0t.c|tenant|',
  m365_group: 'c:0o.c|federateddirectoryclaimprovider|'
};

/** Only explicit directory member claims are accepted; never Everyone or owner claims. */
export const parseDirectoryGroupClaim = (loginName: string): AssignmentPrincipal | undefined => {
  const login = loginName.trim().toLowerCase();
  for (const kind of ['security_group', 'm365_group'] as const) {
    if (login.startsWith(prefixes[kind])) {
      const id = login.slice(prefixes[kind].length);
      if (objectIdPattern.test(id)) return { principalKind: kind, principalIdentifier: id };
    }
  }
  return undefined;
};

export const normalizeAssignmentPrincipal = (input: {
  principalKind?: unknown; principalIdentifier?: unknown; principalEmail?: unknown;
}): AssignmentPrincipal => {
  const kind = input.principalKind ?? 'user';
  if (kind !== 'user' && kind !== 'security_group' && kind !== 'm365_group') throw new Error('Unsupported principal kind.');
  const identifier = String(input.principalIdentifier ?? input.principalEmail ?? '').trim().toLowerCase();
  if (kind === 'user') {
    const email = String(input.principalEmail ?? identifier).trim().toLowerCase();
    if (!/^[^\s@|]+@[^\s@|]+\.[^\s@|]+$/.test(email) || email.length > 256 || identifier !== email) {
      throw new Error('A person requires one matching email identity.');
    }
    return { principalKind: kind, principalIdentifier: email, principalEmail: email };
  }
  const claim = parseDirectoryGroupClaim(identifier);
  const id = claim?.principalIdentifier ?? identifier;
  if ((claim && claim.principalKind !== kind) || !objectIdPattern.test(id)) throw new Error('A group requires its matching immutable directory object ID.');
  return { principalKind: kind, principalIdentifier: id };
};

export const principalKey = (input: Parameters<typeof normalizeAssignmentPrincipal>[0]): string => {
  const principal = normalizeAssignmentPrincipal(input);
  return `${principal.principalKind}:${principal.principalIdentifier}`;
};

export const directoryGroupLogin = (input: Parameters<typeof normalizeAssignmentPrincipal>[0]): string => {
  const principal = normalizeAssignmentPrincipal(input);
  if (principal.principalKind === 'user') throw new Error('A directory group is required.');
  return prefixes[principal.principalKind] + principal.principalIdentifier;
};

export interface GroupAssignmentRule {
  groupKey: string;
  principalKind?: AssignmentPrincipalKind;
  principalIdentifier?: string;
  principalEmail?: string;
  targetMode: 'all_instances' | 'instances';
  targetInstanceIds: string[];
  isActive?: boolean;
}

/** Disjoint site assignments may use different groups; overlapping roles may not. */
export const assertSingleDirectoryGroupPerRole = (rules: readonly GroupAssignmentRule[]): void => {
  const groups = rules.filter(rule => rule.isActive !== false && rule.principalKind && rule.principalKind !== 'user');
  groups.forEach((rule, index) => {
    for (const other of groups.slice(index + 1)) {
      const overlap = rule.targetMode === 'all_instances' || other.targetMode === 'all_instances'
        || rule.targetInstanceIds.some(id => other.targetInstanceIds.includes(id));
      if (rule.groupKey === other.groupKey && overlap && principalKey(rule) !== principalKey(other)) {
        throw new Error(`Only one directory group may be assigned to role ${rule.groupKey} on an installation.`);
      }
    }
  });
};
